import merge from 'lodash/merge';
import { combineReducers } from 'redux';
import { normalize } from 'normalizr';

import { schoolSchema } from '../../schemas';
import {
  GET_SCHOOL_HOT_POSTS_FULFILLED,
  GET_SCHOOL_HOT_POSTS_PENDING,
  GET_SCHOOL_HOT_POSTS_REJECTED,
  GET_SCHOOL_INFO_FULFILLED,
  GET_SCHOOL_INFO_PENDING,
  GET_SCHOOL_INFO_REJECTED,
  GET_SCHOOL_INTERESTS_FULFILLED,
  GET_SCHOOL_INTERESTS_PENDING,
  GET_SCHOOL_INTERESTS_REJECTED,
  GET_SCHOOL_POSTS_FULFILLED,
  GET_SCHOOL_POSTS_PENDING,
  GET_SCHOOL_POSTS_REJECTED,
  GET_SCHOOL_RECENT_ACTIVITIES_FULFILLED,
  GET_SCHOOL_RECENT_ACTIVITIES_PENDING,
  GET_SCHOOL_RECENT_ACTIVITIES_REJECTED,
  GET_SCHOOL_TOPICS_FULFILLED,
  GET_SCHOOL_TOPICS_PENDING,
  GET_SCHOOL_TOPICS_REJECTED,
} from '../../types';

// State interfaces
interface InfoState {
  id: string | null;
  entities: any;
  isFetching: boolean;
  error: any | null;
}

interface HotPostsState {
  school: string | null;
  data: any[];
  sinceDaysAgo: number;
  offset: number;
  limit: number;
  isFetching: boolean;
  error: any | null;
}

interface PostsState {
  school: string | null;
  data: any[];
  total: number;
  offset: number;
  limit: number;
  isFetching: boolean;
  error: any | null;
}

interface TopicsState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface InterestsState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface RecentActivitiesState {
  data: {
    date: any[];
    times: any[];
  };
  isFetching: boolean;
  error: any | null;
}

interface SchoolState {
  hotPosts: HotPostsState;
  info: InfoState;
  interests: InterestsState;
  posts: PostsState;
  recentActivities: RecentActivitiesState;
  topics: TopicsState;
}

// Action interfaces
interface PendingAction {
  type: typeof GET_SCHOOL_INFO_PENDING | typeof GET_SCHOOL_POSTS_PENDING |
        typeof GET_SCHOOL_HOT_POSTS_PENDING | typeof GET_SCHOOL_INTERESTS_PENDING |
        typeof GET_SCHOOL_TOPICS_PENDING | typeof GET_SCHOOL_RECENT_ACTIVITIES_PENDING;
}

interface RejectedAction {
  type: typeof GET_SCHOOL_INFO_REJECTED | typeof GET_SCHOOL_POSTS_REJECTED |
        typeof GET_SCHOOL_HOT_POSTS_REJECTED | typeof GET_SCHOOL_INTERESTS_REJECTED |
        typeof GET_SCHOOL_TOPICS_REJECTED | typeof GET_SCHOOL_RECENT_ACTIVITIES_REJECTED;
  error: any;
}

interface GetSchoolInfoFulfilledAction {
  type: typeof GET_SCHOOL_INFO_FULFILLED;
  payload: {
    data: any;
  };
}

interface GetSchoolPostsFulfilledAction {
  type: typeof GET_SCHOOL_POSTS_FULFILLED;
  payload: {
    data: {
      total: number;
      content: any[];
    };
  };
  meta: {
    school: string;
    offset: number;
    limit: number;
  };
}

interface GetSchoolHotPostsFulfilledAction {
  type: typeof GET_SCHOOL_HOT_POSTS_FULFILLED;
  payload: {
    data: any[];
  };
  meta: {
    school: string;
    sinceDaysAgo: number;
    offset: number;
    limit: number;
  };
}

interface GetSchoolDataFulfilledAction {
  type: typeof GET_SCHOOL_INTERESTS_FULFILLED | typeof GET_SCHOOL_TOPICS_FULFILLED;
  payload: {
    data: any[];
  };
}

interface GetSchoolRecentActivitiesFulfilledAction {
  type: typeof GET_SCHOOL_RECENT_ACTIVITIES_FULFILLED;
  payload: {
    data: {
      date: any[];
      times: any[];
    };
  };
}

type InfoAction = PendingAction | GetSchoolInfoFulfilledAction | RejectedAction;
type PostsAction = PendingAction | GetSchoolPostsFulfilledAction | RejectedAction;
type HotPostsAction = PendingAction | GetSchoolHotPostsFulfilledAction | RejectedAction;
type DataAction = PendingAction | GetSchoolDataFulfilledAction | RejectedAction;
type RecentActivitiesAction = PendingAction | GetSchoolRecentActivitiesFulfilledAction | RejectedAction;

const INITIAL_STATE = {
  INFO: {
    id: null,
    entities: {},
    isFetching: false,
    error: null,
  },
  HOT_POSTS: {
    school: null,
    data: [],
    sinceDaysAgo: 1,
    offset: 0,
    limit: 0,
    isFetching: false,
    error: null,
  },
  POSTS: {
    school: null,
    data: [],
    total: 0,
    offset: 0,
    limit: 0,
    isFetching: false,
    error: null,
  },
  TOPICS: {
    data: [],
    isFetching: false,
    error: null,
  },
  INTERESTS: {
    data: [],
    isFetching: false,
    error: null,
  },
  RECENT_ACTIVITIES: {
    data: {
      date: [],
      times: [],
    },
    isFetching: false,
    error: null,
  },
};

const info = (state: InfoState = INITIAL_STATE.INFO, action: InfoAction): InfoState => {
  switch (action.type) {
    case GET_SCHOOL_INFO_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_SCHOOL_INFO_FULFILLED: {
      const {
        data,
      } = action.payload;
      if (!data) {
        return INITIAL_STATE.INFO;
      }
      const {
        entities,
        result,
      } = normalize(data, schoolSchema);
      return {
        entities: merge(state.entities, entities),
        id: result,
        isFetching: false,
        error: null,
      };
    }
    case GET_SCHOOL_INFO_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const posts = (state: PostsState = INITIAL_STATE.POSTS, action: PostsAction): PostsState => {
  switch (action.type) {
    case GET_SCHOOL_POSTS_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_SCHOOL_POSTS_FULFILLED: {
      const {
        school,
        offset,
        limit,
      } = action.meta;
      const {
        data: {
          total,
          content,
        },
      } = action.payload;
      return {
        school,
        data: content,
        total,
        offset,
        limit,
        isFetching: false,
        error: null,
      };
    }
    case GET_SCHOOL_POSTS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const hotPosts = (state: HotPostsState = INITIAL_STATE.HOT_POSTS, action: HotPostsAction): HotPostsState => {
  switch (action.type) {
    case GET_SCHOOL_HOT_POSTS_PENDING:
      return {
        ...INITIAL_STATE.HOT_POSTS,
        isFetching: true,
      };
    case GET_SCHOOL_HOT_POSTS_FULFILLED: {
      const {
        school,
        sinceDaysAgo,
        offset,
        limit,
      } = action.meta;
      const {
        data,
      } = action.payload;
      return {
        school,
        data,
        sinceDaysAgo,
        offset,
        limit,
        isFetching: false,
        error: null,
      };
    }
    case GET_SCHOOL_HOT_POSTS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const interests = (state: InterestsState = INITIAL_STATE.INTERESTS, action: DataAction): InterestsState => {
  switch (action.type) {
    case GET_SCHOOL_INTERESTS_PENDING:
      return {
        ...INITIAL_STATE.INTERESTS,
        isFetching: true,
      };
    case GET_SCHOOL_INTERESTS_FULFILLED: {
      const {
        data,
      } = action.payload;
      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_SCHOOL_INTERESTS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const topics = (state: TopicsState = INITIAL_STATE.TOPICS, action: DataAction): TopicsState => {
  switch (action.type) {
    case GET_SCHOOL_TOPICS_PENDING:
      return {
        ...INITIAL_STATE.TOPICS,
        isFetching: true,
      };
    case GET_SCHOOL_TOPICS_FULFILLED: {
      const {
        data,
      } = action.payload;

      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_SCHOOL_TOPICS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const recentActivities = (state: RecentActivitiesState = INITIAL_STATE.RECENT_ACTIVITIES, action: RecentActivitiesAction): RecentActivitiesState => {
  switch (action.type) {
    case GET_SCHOOL_RECENT_ACTIVITIES_PENDING:
      return {
        ...INITIAL_STATE.RECENT_ACTIVITIES,
        isFetching: true,
      };
    case GET_SCHOOL_RECENT_ACTIVITIES_FULFILLED: {
      const {
        data,
      } = action.payload;
      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_SCHOOL_RECENT_ACTIVITIES_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

export default combineReducers<SchoolState>({
  hotPosts,
  info,
  interests,
  posts,
  recentActivities,
  topics,
});